import { useEffect, useMemo, useState } from 'react';
import type { AskBlock } from '@/api/models';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/field';
import { Markdown } from '@/lib/markdown/Markdown';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAnswerAsk } from '@/api/queries';

/**
 * The inline question.
 *
 * When the agent stops mid-run for a question or a permission, it appears here
 * and the client answers it here, and the same run carries on. That is the
 * whole feature - there is no separate inbox and no notification to chase.
 *
 * The ask is a database row, not a socket message. That is why closing the tab
 * and coming back an hour later works: the question replays as a normal event
 * and this card renders it again. It is also why answering is a POST rather
 * than a socket write.
 *
 * If nobody answers before it expires the platform answers on the client's
 * behalf - with an explanation the agent can act on, not a kill - so this card
 * must stop saying "waiting for you" the moment that happens. It does: the
 * server sends `ask_resolved` and the card unmounts.
 *
 * Two shapes:
 *  * closed-ended, when the ask carries `options` - buttons, because a
 *    non-technical person should not have to guess the exact wording;
 *  * open, otherwise - a text box.
 *
 * A permission-shaped ask (one that offers a decision) is phrased as a
 * permission: the answer text travels with a denial rather than instead of it,
 * so the agent finishes coherently either way.
 */

interface AskCardProps {
  projectId: number;
  ask: AskBlock;
  answerableByClient: boolean;
}

const PERMISSION_HINT = 'The work is paused until you answer.';

export function AskCard({ projectId, ask, answerableByClient }: AskCardProps) {
  const [text, setText] = useState('');
  const [choice, setChoice] = useState<string | null>(null);
  const mutation = useAnswerAsk(projectId);

  const options = useMemo(() => ask.options ?? [], [ask.options]);
  const isClosed = options.length > 0;

  // A new question replaces whatever was half-typed for the previous one.
  useEffect(() => {
    setText('');
    setChoice(null);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask.ask_id]);

  const expiresAt = ask.expires_at ? new Date(ask.expires_at) : null;
  const expiresSoon = expiresAt ? expiresAt.getTime() - Date.now() < 3 * 60_000 : false;

  const submit = (answer: string, decision?: 'allow' | 'deny') => {
    const trimmed = answer.trim();
    if (!trimmed) return;
    mutation.mutate({ askId: ask.ask_id, answer: trimmed, decision });
  };

  if (ask.answered) return null;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const gone = error && error.httpStatus === 409;

  return (
    // The parked question is the one thing on the screen waiting on the reader,
    // so it is the one thing that wears the accent. Blush frames a white card:
    // the wash says "this is yours to answer" from across the room, and the
    // white inside keeps the question itself as readable as everything else.
    <section className="mb-[22px] rounded-lg bg-blush p-1.5" aria-label="Question from the workspace">
      <div className="rounded-md bg-bone-raised px-[22px] pb-4 pt-5">
        <p className="eyebrow mb-2 text-signal">A question for you</p>
        <Markdown
          source={ask.question}
          className="text-[1.1875rem] font-semibold leading-[1.32] tracking-[-0.02em] text-graphite-900"
        />

        {!answerableByClient ? (
          <p className="mt-3 text-[0.90625rem] text-graphite-500">
            Your reviewer is answering this one. Nothing is needed from you.
          </p>
        ) : gone ? (
          <p className="mt-3 text-[0.90625rem] text-graphite-500">
            This question has already been dealt with — the work carried on without it. Nothing is needed from you.
          </p>
        ) : (
          <>
            <p className="mt-2 text-[0.90625rem] text-graphite-500">
              {expiresAt ? (
                <>
                  Answer by{' '}
                  <span
                    className={cn('font-mono text-[0.8125rem] tabular-nums', expiresSoon ? 'text-alarm' : 'text-graphite-500')}
                  >
                    {expiresAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>{' '}
                  and the same work carries on.
                </>
              ) : (
                PERMISSION_HINT
              )}
            </p>

            {isClosed ? (
              <div className="mt-4 flex flex-wrap gap-2.5">
                {options.map((option) => (
                  <Button
                    key={option.value}
                    variant={choice === option.value ? 'primary' : 'secondary'}
                    className={cn(choice !== option.value && 'bg-bone shadow-none hover:shadow-[inset_0_0_0_1.5px_var(--rule-strong)]')}
                    loading={mutation.isPending && choice === option.value}
                    disabled={mutation.isPending}
                    onClick={() => {
                      setChoice(option.value);
                      submit(option.value);
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            ) : null}

            <form
              className="mt-4"
              onSubmit={(event) => {
                event.preventDefault();
                submit(text);
              }}
            >
              <label htmlFor={`ask-${ask.ask_id}`} className="sr-only">
                Your answer
              </label>
              <Textarea
                id={`ask-${ask.ask_id}`}
                rows={3}
                value={text}
                disabled={mutation.isPending}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault();
                    submit(text);
                  }
                }}
                placeholder={isClosed ? 'Or answer in your own words' : 'Answer in your own words'}
                maxLength={4000}
              />
              <div className="mt-[18px] flex flex-wrap items-center gap-3">
                <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!text.trim()}>
                  Send answer
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={mutation.isPending}
                  onClick={() =>
                    submit(
                      text.trim() ||
                        'No — do not do that. Please carry on with the rest of the request without it.',
                      'deny',
                    )
                  }
                >
                  Do not do that
                </Button>
              </div>
            </form>

            {error && !gone ? <p className="mt-2 text-sm text-alarm">{error.message}</p> : null}
          </>
        )}
      </div>
    </section>
  );
}
